import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { SCHOOLS } from '../Schools/tokens';
import { formatLastGenerated } from '../Reports/lastGenerated';
import { SCORE_UNIT_OPTIONS } from '../ProgramEvaluation/components/ScoreUnitToggle';
import { fetchPEGenerations, type PEGeneration } from './peAdminApi';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

const UNIT_MODE_LABELS = new Map(SCORE_UNIT_OPTIONS.map(({ value, label }) => [value as string, label]));

export function sortByNewestGeneration(rows: PEGeneration[]): PEGeneration[] {
  const time = (row: PEGeneration) => {
    const parsed = row.generatedAt ? Date.parse(row.generatedAt) : NaN;
    return Number.isNaN(parsed) ? -Infinity : parsed;
  };
  return [...rows].sort((a, b) => time(b) - time(a));
}

type Props = {
  source?: () => Promise<PEGeneration[]>;
};

const bodyText = {
  fontFamily: SCHOOLS.font,
  fontSize: 16,
  lineHeight: '24px',
  color: SCHOOLS.muted,
} as const;

export function AdminProgramEvaluationsPage({ source = fetchPEGenerations }: Props = {}) {
  const [rows, setRows] = useState<PEGeneration[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    source()
      .then((result) => {
        if (!cancelled) setRows(sortByNewestGeneration(result));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  return (
    <Box sx={{ display: 'flex', bgcolor: SCHOOLS.surface, minHeight: HEIGHT_BELOW_BARS }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: '24px 32px' }}>
        <Typography
          component="h1"
          sx={{ fontFamily: SCHOOLS.font, fontSize: 24, fontWeight: 700, color: SCHOOLS.ink }}
        >
          Program Evaluations
        </Typography>
        <Typography sx={{ ...bodyText, fontSize: 14, lineHeight: '20px', mb: 2 }}>
          Every program evaluation generated across districts. A report belongs to a district, so
          colleagues generating for the same district and school year share one report history.
        </Typography>
        {failed && (
          <Typography data-testid="admin-pe-error" sx={bodyText}>
            Could not load program evaluations. Refresh to try again.
          </Typography>
        )}
        {!failed && rows === null && <CircularProgress data-testid="admin-pe-loading" size={24} />}
        {!failed && rows !== null && rows.length === 0 && (
          <Typography data-testid="admin-pe-empty" sx={bodyText}>
            No program evaluations have been generated yet.
          </Typography>
        )}
        {!failed && rows !== null && rows.length > 0 && <GenerationsTable rows={rows} />}
      </Box>
    </Box>
  );
}

const HEADERS = ['District', 'School year', 'Unit mode', 'Generations', 'Last generated'];

function firstFilled(...values: (string | undefined)[]): string {
  for (const value of values) {
    const trimmed = value?.trim() ?? '';
    if (trimmed !== '') return trimmed;
  }
  return '';
}

function generationLabel(row: PEGeneration): string {
  return firstFilled(row.districtName, row.email, row.cognitoSub, row.districtId);
}

function generationPartition(row: PEGeneration): string {
  return firstFilled(row.districtId, row.cognitoSub);
}

const cellSx = {
  fontFamily: SCHOOLS.font,
  fontSize: 14,
  color: SCHOOLS.ink,
  borderBottom: `1px solid ${SCHOOLS.border}`,
} as const;

function GenerationsTable({ rows }: { rows: PEGeneration[] }) {
  return (
    <Table
      size="small"
      sx={{ bgcolor: SCHOOLS.cardBg, borderRadius: '4px', boxShadow: SCHOOLS.cardShadow }}
    >
      <TableHead>
        <TableRow>
          {HEADERS.map((label) => (
            <TableCell key={label} sx={{ ...cellSx, fontWeight: 700 }}>
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const partition = generationPartition(row);
          const label = generationLabel(row);
          return (
            <TableRow key={`${partition}#${row.schoolYear ?? ''}`} data-testid="admin-pe-row">
              <TableCell sx={cellSx}>
                {row.schoolYear && partition ? (
                  <Link
                    component={RouterLink}
                    to={`/admin/program-evaluations/${encodeURIComponent(partition)}/${encodeURIComponent(row.schoolYear)}`}
                  >
                    {label}
                  </Link>
                ) : (
                  label
                )}
              </TableCell>
              <TableCell sx={cellSx}>{row.schoolYear || '—'}</TableCell>
              <TableCell sx={cellSx}>
                {row.unitMode ? UNIT_MODE_LABELS.get(row.unitMode) ?? row.unitMode : '—'}
              </TableCell>
              <TableCell sx={cellSx}>{row.generations}</TableCell>
              <TableCell sx={cellSx}>
                {(row.generatedAt && formatLastGenerated(row.generatedAt)) || '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
